import { useRef } from 'react';
import { createFilter } from 'react-select';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  LanguageVariants,
  Select,
  Text,
  TextArea
} from 'components/common/form';

import type { FormApi, FormState } from 'informed';
import type { GetCompetencyProfilesRes } from 'shared-types/responses';
import type { AddOrUpdateCompetencyProfileReq } from 'shared-types/requests';
import type { LevelSeparatedCompetency } from 'lib/competencies/getLevelSeparatedCompetencies';

type AddOrUpdateCompetencyProfileFormState = Pick<
  AddOrUpdateCompetencyProfileReq,
  'description' | 'descriptionLocales' | 'title' | 'titleLocales'
> & {
  categories: string[];
  competencies: string[];
};

interface Props {
  categoryOptions: string[];
  competencyOptions: LevelSeparatedCompetency[];
  competencyProfile?: GetCompetencyProfilesRes['competencyProfiles'][0];
  onSubmit: () => Promise<void>;
}

const CompetencyProfilesAddOrUpdate = ({
  categoryOptions,
  competencyOptions,
  competencyProfile = null,
  onSubmit
}: Props) => {
  const { dispatch } = useDispatch();

  const { language, messages } = useReduxState();

  const formApi = useRef<FormApi<AddOrUpdateCompetencyProfileFormState>>(undefined);

  const getCompetencyOptions = () => {
    if (!formApi.current) return [];

    const { values } = formApi.current.getState();

    const options: {
      isDisabled: boolean;
      label: React.ReactElement<any>;
      value: string;
    }[] = [];
    competencyOptions.forEach((competency) => {
      const isCompetencyIncluded = values.competencies?.find((value) =>
        value.startsWith(`${competency.id}-`)
      );

      const isExactIncluded =
        isCompetencyIncluded ===
        `${competency.id}-${competency.level?.id || 0}`;
      if (isExactIncluded) return;

      options.push({
        isDisabled: Boolean(isCompetencyIncluded),
        label: (
          <span
            {...(Boolean(isCompetencyIncluded)
              ? {
                  'data-tooltip':
                    messages.competencyProfilesSelectOptionDisabled
                }
              : {})}
          >
            {competency.name}
            {competency.level ? (
              <span style={{ fontWeight: 400 }}>{`: ${
                competency.level.nameLocales?.[language] ||
                competency.level.name
              }`}</span>
            ) : (
              ''
            )}
          </span>
        ),
        value: `${competency.id}-${competency.level?.id || 0}`
      });
    });

    return options;
  };

  const handleSubmit = async (
    values: AddOrUpdateCompetencyProfileFormState
  ) => {
    const competencies = values.competencies?.map((competencyValue) => {
      const valueParts = competencyValue.split('-');
      return {
        id: Number(valueParts[0]),
        levelId: Number(valueParts[1]) || null
      };
    });

    await api.post(
      `/admin/competency-profiles${
        competencyProfile ? `/${competencyProfile.id}` : ''
      }`,
      { ...values, categories: values.categories || [], competencies }
    );
    await onSubmit();

    dispatch(
      actions.addNotice({
        text: competencyProfile
          ? messages.competencyProfilesEditSuccess
          : messages.competencyProfilesAddSuccess,
        type: 'success'
      })
    );
  };

  const validateCompetencies = (value: string[]) => {
    return value?.length ? undefined : messages.commonFieldRequired;
  };

  const validateTitle = async (value: string) => {
    if (!value?.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/competency-profiles?title=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  const buttonProps = competencyProfile
    ? {
        buttonIcon: 'pen',
        buttonIsDropdown: true,
        buttonText: messages.competencyProfilesEdit,
        modalActionText: messages.commonSave,
        modalTitle: messages.competencyProfilesEdit
      }
    : {
        buttonIcon: 'plus',
        buttonText: messages.competencyProfilesAdd,
        modalActionText: messages.commonAdd,
        modalTitle: messages.competencyProfilesAdd
      };

  const initialValues = competencyProfile
    ? {
        categories: competencyProfile.categories,
        competencies: competencyProfile.competencies.map(
          (competency) => `${competency.id}-${competency.level?.id || 0}`
        ),
        description: competencyProfile.description,
        descriptionLocales: competencyProfile.descriptionLocales,
        title: competencyProfile.title,
        titleLocales: competencyProfile.titleLocales
      }
    : { categories: [], competencies: [] };

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: 'any' as const,
    stringify: (option) => {
      const competency = competencyOptions.find(
        (competencyOption) =>
          `${competencyOption.id}-${competencyOption.level?.id || 0}` ===
          option.value
      );
      return `${competency?.name} ${
        competency?.level?.nameLocales?.[language] ||
        competency?.level?.name ||
        ''
      }`;
    },
    trim: true
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonSize="header"
      initialValues={initialValues}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<AddOrUpdateCompetencyProfileFormState>) =>
        (formApi.current = form)
      }
      {...buttonProps}
    >
      {(formState: FormState<AddOrUpdateCompetencyProfileFormState>) => (
        <>
          <FormGroup label={messages.commonTitle}>
            <Text
              customValidateAsync={validateTitle}
              field="title"
              id="title"
              required={true}
              validateOnBlur={true}
              validateOnChange={true}
            />

            <LanguageVariants field="titleLocales" type="text" />
          </FormGroup>

          <FormGroup label={messages.commonDescription}>
            <TextArea field="description" id="description" />

            <LanguageVariants field="descriptionLocales" type="textarea" />
          </FormGroup>

          <FormGroup label={messages.commonCategories}>
            <Select
              creatable={true}
              field="categories"
              id="profile-categories"
              isMulti={true}
              options={categoryOptions.map((category) => ({
                label: category,
                value: category
              }))}
            />
          </FormGroup>

          <FormGroup
            label={messages.analyticsCompetencies}
            style={{ marginBottom: 20 }}
          >
            <Select
              error={formState.errors?.competencies}
              field="competencies"
              filterOption={createFilter(filterConfig)}
              id="profile-competencies-add-competencies"
              isMulti={true}
              isSearchable={true}
              options={getCompetencyOptions()}
              showLabelForSelectedElement={true}
              validate={validateCompetencies}
              validateOnBlur={true}
              validateOnChange={true}
              value={formState?.values?.competencies?.map((competencyValue) => {
                const competency = competencyOptions.find(
                  (competencyOption) =>
                    `${competencyOption.id}-${
                      competencyOption.level?.id || 0
                    }` === competencyValue
                );

                return {
                  label: (
                    <span>
                      {competency.name}
                      {competency.level ? (
                        <span style={{ fontWeight: 400 }}>{`: ${
                          competency.level.nameLocales?.[language] ||
                          competency.level.name
                        }`}</span>
                      ) : (
                        ''
                      )}
                    </span>
                  ),
                  value: `${competency.id}-${competency.level?.id || 0}`
                };
              })}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CompetencyProfilesAddOrUpdate;
